import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AppHeader from "../common/AppHeader";
import BottomNav from "../common/BottomNav";
import { getLeadsBySpId } from "../../services/leadService";
import styles from "../css/Leads.module.css";

const FILTERS = [
  { key: "all", labelKey: "filter_all" },
  { key: "today", labelKey: "filter_today" },
  { key: "week", labelKey: "filter_week" },
  { key: "month", labelKey: "filter_month" },
];

function filterLeads(leads, key) {
  const now = new Date();
  if (key === "today") {
    return leads.filter(
      (l) => new Date(l.createdAt).toDateString() === now.toDateString(),
    );
  }
  if (key === "week") {
    return leads.filter(
      (l) => now - new Date(l.createdAt) <= 7 * 24 * 60 * 60 * 1000,
    );
  }
  if (key === "month") {
    const m = now.getMonth();
    const y = now.getFullYear();
    return leads.filter((l) => {
      const d = new Date(l.createdAt);
      return d.getMonth() === m && d.getFullYear() === y;
    });
  }
  return leads;
}

function formatTime(iso, t, language) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return t("Leads.time_minutes_ago", { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("Leads.time_hours_ago", { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return t("Leads.time_days_ago", { count: days });
  return new Date(iso).toLocaleDateString(
    language === "hi" ? "hi-IN" : "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    },
  );
}

function getActionLabel(actionType, t) {
  if (!actionType) return t("Leads.action_interaction");
  if (actionType === "CONTACT ENABLE") return t("Leads.action_contact_viewed");
  return actionType
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatServiceSubtitle(listingService) {
  if (!listingService) return "";
  const category = listingService.category || "";
  const firstSubCategory =
    (listingService.subCategory || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)[0] || "";

  if (category && firstSubCategory) return `${category} • ${firstSubCategory}`;
  return category || firstSubCategory;
}

export default function Leads() {
  const { t, i18n } = useTranslation();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");

  const fetchLeads = useCallback(async () => {
    if (!user.spId) return;
    setLoading(true);
    const data = await getLeadsBySpId(user.spId);
    data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    setLeads(data);
    setLoading(false);
  }, [user.spId]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const todayCount = leads.filter(
    (l) => new Date(l.createdAt).toDateString() === new Date().toDateString(),
  ).length;

  const weekCount = leads.filter(
    (l) => Date.now() - new Date(l.createdAt) <= 7 * 24 * 60 * 60 * 1000,
  ).length;

  const filtered = filterLeads(leads, activeFilter);

  return (
    <div className={styles.page}>
      <AppHeader
        title="Leads.title"
        showBack={true}
        showRefresh={true}
        onRefresh={fetchLeads}
        refreshing={loading}
      />

      {/* Stats Bar */}
      <div className={styles.statsBar}>
        <div className={styles.stat}>
          <span className={styles.statNum}>{leads.length}</span>
          <span className={styles.statLabel}>{t("Leads.total")}</span>
        </div>
        <div className={styles.statDivider} />
        <div className={styles.stat}>
          <span className={styles.statNum}>{todayCount}</span>
          <span className={styles.statLabel}>{t("Leads.today")}</span>
        </div>
        <div className={styles.statDivider} />
        <div className={styles.stat}>
          <span className={styles.statNum}>{weekCount}</span>
          <span className={styles.statLabel}>{t("Leads.this_week")}</span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className={styles.filterTabs}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`${styles.filterTab} ${
              activeFilter === f.key ? styles.filterTabActive : ""
            }`}
            onClick={() => setActiveFilter(f.key)}
          >
            {t(`Leads.${f.labelKey}`)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className={styles.content}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner} />
            <span>{t("Leads.loading")}</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>
            <span className={styles.emptyIcon}>📭</span>
            <h3>{t("Leads.empty_title")}</h3>
            <p>
              {activeFilter === "all"
                ? t("Leads.empty_all")
                : t("Leads.empty_filtered")}
            </p>
          </div>
        ) : (
          <>
            <p className={styles.resultCount}>
              {t("Leads.result_count", { count: filtered.length })}
            </p>
            <div className={styles.leadsList}>
              {filtered.map((lead) => (
                <div key={lead.id} className={styles.leadCard}>
                  {/* Card Top */}
                  <div className={styles.leadCardMain}>
                    <div className={styles.avatar}>
                      {lead.userName ? lead.userName[0].toUpperCase() : "U"}
                    </div>
                    <div className={styles.userInfo}>
                      <div className={styles.nameRow}>
                        <span className={styles.userName}>
                          {lead.userName || t("Leads.unknown_user")}
                        </span>
                        <span className={styles.actionBadge}>
                          {getActionLabel(lead.actionType, t)}
                        </span>
                      </div>
                      <span className={styles.userPhone}>
                        {lead.userPhone || "—"}
                      </span>
                      {lead.city && (
                        <span className={styles.userCity}>📍 {lead.city}</span>
                      )}
                    </div>
                  </div>

                  {/* Service Info */}
                  {lead.listingService && (
                    <div className={styles.serviceDetails}>
                      <span className={styles.serviceLabel}>
                        {t("Leads.service")}
                      </span>
                      <span className={styles.serviceName}>
                        {lead.listingService.businessName ||
                          t("Leads.service_listing")}
                      </span>
                      {formatServiceSubtitle(lead.listingService) && (
                        <span className={styles.serviceMeta}>
                          {formatServiceSubtitle(lead.listingService)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Card Footer */}
                  <div className={styles.cardFooter}>
                    <div className={styles.footerMeta}>
                      {lead.deviceType && (
                        <span className={styles.metaDevice}>
                          {lead.deviceType}
                        </span>
                      )}
                      <span className={styles.metaTime}>
                        {formatTime(lead.createdAt, t, i18n.language)}
                      </span>
                    </div>
                    {lead.userPhone && (
                      <div className={styles.cardActions}>
                        <a
                          href={`https://wa.me/91${lead.userPhone?.replace(/\D/g, "")}`}
                          className={styles.actionWa}
                          onClick={(e) => e.stopPropagation()}
                        >
                          💬 WhatsApp
                        </a>
                        <a
                          href={`tel:${lead.userPhone}`}
                          className={styles.actionCall}
                          onClick={(e) => e.stopPropagation()}
                        >
                          📞 Call
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
